import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import CustomSwitch from "../../../../components/CustomSwitch";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import silverfort_api from "./images/silverfort_api.png";
import silverfort_certificate from "./images/silverfort_certificate.png";
import silverfort_generate_key from "./images/silverfort_generate_key.png";

const SILVERFORT_CODE_NAME = "silverfort";

export default function SilverfortIntegration() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const {
    registerSilverfortCloudIntegration,
    registerSilverfortOnPremIntegration,
  } = useAPI();
  const [registerIsLoading, setRegisterIsLoading] = useState(false);
  const [appUserId, setAppUserId] = useState("");
  const [appUserSecret, setAppUserSecret] = useState("");
  const [apiEndpoint, setApiEndpoint] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [isOnPrem, setIsOnPrem] = useState(false);
  const { isIntegrated, isIntegratedLoading } =
    useIsIntegrated(SILVERFORT_CODE_NAME);
  const guideMode = searchParams.has("viewonly");
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const handleOnPremChange = (event) => {
    event.stopPropagation();
    setIsOnPrem((prev) => !prev);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!appUserId || !appUserSecret || !apiEndpoint) {
      Toast.error("Please fill in all required fields");
      return;
    }

    setRegisterIsLoading(true);
    try {
      if (isOnPrem) {
        await registerSilverfortOnPremIntegration(
          appUserId,
          appUserSecret,
          apiEndpoint
        );
      } else {
        await registerSilverfortCloudIntegration(
          appUserId,
          appUserSecret,
          apiEndpoint
        );
      }
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("Silverfort registered successfully");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setRegisterIsLoading(false);
    }
  };

  return (
    <Box>
      <Guideline
        title={isIntegrated ? "" : "Silverfort integration instructions"}
        image={logos.SilverfortLogo}
        iconBorderRadius={4}
        name={"Silverfort"}
        content={
          isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Silverfort is registered!</Typography>
            </Box>
          ) : (
            <Box>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <form onSubmit={onSubmit}>
                <GuidelineStepByStep>
                  <GuidelineStep title="Navigate to Settings > Silverfort API">
                    <Typography variant="body2">
                      Go to Admin Console and select and access the API settings
                      page.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: silverfort_api,
                          altext: "Silverfort API Settings",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>

                  <GuidelineStep title="Create an API Key">
                    <Typography variant="body2">
                      Click the <b>"+ Create Key"</b> button to open the Create
                      Key dialog.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: silverfort_generate_key,
                          altext: "Create Key Dialog",
                          note: "Click Create Key and fill in the required fields",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2" component="div">
                      In the Create Key dialog:
                      <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                        <Box component="li" sx={{ marginBottom: "4px" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span>
                              Assign the API a name: <b>Breeze</b>
                            </span>
                            <CustomCopyButton
                              copyTitle="App Name"
                              copyText="Breeze"
                              width="14px"
                            />
                          </Box>
                        </Box>
                        <Box component="li">
                          Select a category and permissions
                        </Box>
                      </Box>
                    </Typography>
                  </GuidelineStep>

                  <GuidelineStep title="Store secret values">
                    <Typography variant="body2">
                      Copy and save the <b>App User ID</b> and{" "}
                      <b>App User Secret</b> and paste them below.
                    </Typography>
                    <GuidelineInstruction text="Copy the App User ID and App User Secret:" />
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <FormControlLabel
                        sx={{ margin: 0, gap: 2, alignItems: "center" }}
                        disabled={guideMode}
                        control={
                          <CustomSwitch
                            checked={isOnPrem}
                            onChange={handleOnPremChange}
                            disabled={guideMode}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ cursor: guideMode ? "default" : "pointer" }}
                          >
                            On-Prem Deployment
                          </Typography>
                        }
                        labelPlacement="start"
                      />
                    </Box>
                    <TextField
                      required
                      fullWidth
                      variant="filled"
                      name="appUserId"
                      label="App User ID"
                      type="text"
                      value={appUserId}
                      onChange={(e) => setAppUserId(e.target.value)}
                      autoComplete="off"
                      disabled={guideMode}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      required
                      fullWidth
                      variant="filled"
                      name="appUserSecret"
                      label="App User Secret"
                      type={showSecret ? "text" : "password"}
                      value={appUserSecret}
                      onChange={(e) => setAppUserSecret(e.target.value)}
                      autoComplete="off"
                      disabled={guideMode}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle secret visibility"
                              onClick={() => setShowSecret((prev) => !prev)}
                            >
                              {showSecret ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    <GuidelineInstruction text="Enter the Silverfort API endpoint URL or IP address (for on-prem deployments):" />
                    <TextField
                      required
                      fullWidth
                      variant="filled"
                      name="apiEndpoint"
                      label="API Endpoint or IP Address"
                      type="text"
                      value={apiEndpoint}
                      onChange={(e) => setApiEndpoint(e.target.value)}
                      autoComplete="off"
                      disabled={guideMode}
                      sx={{ mb: 2 }}
                    />
                  </GuidelineStep>

                  <GuidelineStep title="Click Create"></GuidelineStep>

                  <GuidelineStep title="Set up an Admin Console certificate">
                    <Typography variant="body2">
                      Silverfort API uses a certificate to secure communication
                      with client applications. Set up an Admin Console
                      certificate for each feature you are communicating with.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Access the Admin Console and go to{" "}
                      <b>Settings &gt; Protected Domains</b> or{" "}
                      <b>Settings &gt; Desktop Application</b>, depending on the
                      communication you are setting up.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: silverfort_certificate,
                          altext: "Certificate Setup",
                          note: "Generate and upload certificate for Protected Domains or Desktop Application",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      For each feature:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                      <Box component="li">
                        <Typography variant="body2">
                          Select <b>"Generate Certificate Request"</b>
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">
                          Select <b>"Upload Certificate"</b> and then select the
                          certificate you just generated
                        </Typography>
                      </Box>
                    </Box>
                  </GuidelineStep>

                  <GuidelineStep title="All done, now hit 'Connect' to integrate!">
                    <Box display="flex" justifyContent="center">
                      <ContainedButton
                        type="submit"
                        size="small"
                        color="primary"
                        disabled={
                          guideMode ||
                          !appUserId ||
                          !appUserSecret ||
                          !apiEndpoint
                        }
                        loading={registerIsLoading}
                      >
                        Connect
                      </ContainedButton>
                    </Box>
                  </GuidelineStep>
                </GuidelineStepByStep>
              </form>
            </Box>
          )
        }
      />
    </Box>
  );
}
